import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar, { NAV } from "../components/terminal/Sidebar";
import TopBar from "../components/terminal/TopBar";
import MobileNav from "../components/terminal/MobileNav";
import QuoteStrip from "../components/terminal/QuoteStrip";
import AlertManager from "../components/alerts/AlertManager";
import { useMarket } from "../context/MarketContext";
import { useAlerts } from "../context/AlertsContext";
import { useScanner } from "../context/ScannerContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { api } from "../lib/api";
import { useUiStore } from "../store/uiStore";
import { Circle, Radio, RefreshCw, History, ChevronRight } from "lucide-react";

// Application shell: sidebar (routes + live badges) · top bar · quote strip · routed page · status footer.
export default function Terminal() {
  const { status, feed } = useMarket();
  const { unread } = useAlerts();
  const { matches } = useScanner();
  const { openChart } = useWorkspace();
  const location = useLocation();
  const [clock, setClock] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const [openContracts, setOpenContracts] = useState(0);
  const page = NAV.find((n) => n.to === location.pathname) ?? NAV[0];

  useEffect(() => { document.title = `${page.title} · Fela R.I.O`; }, [page]);
  useEffect(() => {
    const id = setInterval(() => setClock(new Date().toUTCString().split(" ")[4] + " UTC"), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const poll = () => api.get("/trading/status").then(({ data }) => setOpenContracts(data.open_count ?? 0)).catch(() => {});
    poll(); const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, []);

  const badges = { alerts: { count: unread, tone: "sell" }, "demo-trade": { count: openContracts, tone: "buy" }, scanner: { count: matches.length } };

  return (
    <div className="h-dvh w-screen flex bg-background text-foreground overflow-hidden terminal-bg">
      <AlertManager />
      <Sidebar badges={badges} className={sidebarCollapsed ? "hidden" : "hidden md:flex"} />
      <MobileNav open={navOpen} onOpenChange={setNavOpen} badges={badges} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar onSelectSymbol={(s) => openChart(s)} onMenu={() => setNavOpen(true)} />
        {location.pathname === "/chart" ? (
          <div className="flex-1 min-h-0 overflow-hidden" data-testid="chart-workspace"><Outlet /></div>
        ) : (
          <div className="flex-1 overflow-y-auto scrollbar-thin p-3 space-y-3">
            <QuoteStrip onSelect={(s) => openChart(s)} />
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground px-1" data-testid="breadcrumb">
              <span>Fela R.I.O</span><ChevronRight className="h-3 w-3" /><span className="text-foreground font-medium" data-testid="page-title">{page.title}</span>
            </div>
            <Outlet />
          </div>
        )}
        <footer className="hidden md:flex items-center gap-4 px-4 h-9 border-t border-border bg-panel text-[11px] text-muted-foreground shrink-0" data-testid="footer">
          <span className="font-semibold text-foreground">Fela R.I.O <span className="text-muted-foreground">v2.0</span></span>
          <span>Real-time Intelligence Orchestrator</span>
          <div className="ml-auto flex items-center gap-4">
            <span className="flex items-center gap-1.5" data-testid="footer-feed-mode" title="Market data transport">
              {feed.mode === "replay" ? <History className="h-3 w-3 text-primary" /> : feed.mode === "stream" ? <Radio className="h-3 w-3 text-buy" /> : <RefreshCw className={`h-3 w-3 ${feed.mode === "polling" ? "text-primary" : "text-muted-foreground animate-spin"}`} />}
              {feed.mode === "replay" ? "Tick replay" : feed.mode === "stream" ? (feed.source === "relay" ? "Live tick stream · Fela R.I.O relay" : "Live tick stream · Deriv") : feed.mode === "polling" ? "Polling fallback" : "Negotiating feed…"}
            </span>
            <span className="flex items-center gap-1.5" data-testid="footer-connection">
              <Circle className={`h-2 w-2 ${status === "connected" ? "fill-buy text-buy" : "fill-sell text-sell"}`} />
              {status === "connected" ? "Connected · Deriv" : "Reconnecting…"}
            </span>
            <span className="font-mono-nums">{clock}</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
